import { RealEstateDto } from "api/real-estate";
import GenericTable from "components/display/Table/Table";
import { Button } from "components/ui/Button";
import { PenIcon } from "components/icons/Icons";
import { FC } from "react";

import { formatAddress, formatEuroPrice, formatPropertyType } from "../../utils/realEstateFormatters";
import { RealEstateEmptyState } from "./RealEstateEmptyState";

interface RealEstateListProps {
  realEstates: RealEstateDto[];
  selectedRealEstateId: string;
  isLoading: boolean;
  isSelectedRealEstateEligible: boolean;
  onSelect: (id: string) => void;
  onEdit: (realEstate: RealEstateDto) => void;
  t: (key: string) => string;
  // Pagination serveur
  currentPage: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  emptyStateProps: {
    title: string;
    description: string;
    actionLabel: string;
    onAction: () => void;
  };
}

export const RealEstateList: FC<RealEstateListProps> = ({
  realEstates,
  selectedRealEstateId,
  isLoading,
  isSelectedRealEstateEligible,
  onSelect,
  onEdit,
  t,
  currentPage,
  totalItems,
  pageSize,
  onPageChange,
  emptyStateProps
}) => {
  if (!isLoading && realEstates.length === 0) {
    return <RealEstateEmptyState {...emptyStateProps} />;
  }

  return (
    <GenericTable<RealEstateDto>
      config={{
        variant: 'soft',
        loading: isLoading,
        skeletonRowCount: pageSize,
        data: realEstates,
        onRowClick: (row) => onSelect(row.id),
        getRowClassName: (row) => {
          if (row.id !== selectedRealEstateId) return '';
          return isSelectedRealEstateEligible ? 'bg-green-50' : 'bg-red-50';
        },
        columns: [
          {
            key: 'name',
            label: t('REAL_ESTATE.LABELS.NAME'),
            sortable: true,
            render: (_value, row) => (
              <div className="flex items-center gap-3">
                <img
                  src={row.photoUrl || '/assets/images/real_estate_default.png'}
                  alt={row.name}
                  className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                  loading="lazy"
                />
                <div className="flex flex-col min-w-0">
                  <span className="font-medium text-gray-900">{row.name}</span>
                  <span className="text-xs text-gray-500">{formatAddress(row.address)}</span>
                </div>
              </div>
            ),
          },
          {
            key: 'propertyType',
            label: t('REAL_ESTATE.LABELS.TYPE'),
            render: (_value, row) => (
              <span className="text-gray-600">{formatPropertyType(row.propertyType, t)}</span>
            ),
          },
          {
            key: 'area',
            label: t('REAL_ESTATE.LABELS.AREA'),
            align: 'center',
            render: (_value, row) => <span>{row.area} m²</span>,
          },
          {
            key: 'roomCount',
            label: 'Pièces',
            align: 'center',
          },
          {
            key: 'rentAmount',
            label: 'Loyer',
            align: 'right',
            render: (_value, row) =>
              row.rentAmount !== undefined && row.rentAmount > 0 ? (
                <span className="font-medium text-gray-900">{formatEuroPrice(row.rentAmount)}</span>
              ) : (
                <span className="text-gray-400 italic">—</span>
              ),
          },
        ],
        actions: {
          row: [
            {
              label: 'edit',
              render: (row: RealEstateDto, index: number) => (
                <Button key={index} round color="slate" onClick={() => onEdit(row)}>
                  <PenIcon />
                </Button>
              ),
            },
          ],
        },
        pagination: {
          enabled: true,
          pageSize,
          currentPage,
          totalItems,
          totalPages: Math.max(1, Math.ceil(totalItems / pageSize)),
          showInfo: true,
          onPageChange,
        },
      }}
    />
  );
};
